﻿import { Coord, Route, MapController } from "./googleMap";

declare const googleMapsApiKey: string;

const mapControllers: Map<string, MapController> = new Map();

/**
 * Initializes a map with a specified ID and geographic coordinates. Creates a new `MapController`
 * instance, registers it, and initializes the map at the given location. Ensures map controllers
 * are managed centrally for easy access.
 */
async function initMap(mapId: string, latitude: number, longitude: number, autoHeightAdjustment: boolean): Promise<void> {
    const mapController = new MapController(mapId, googleMapsApiKey);
    mapControllers.set(mapId, mapController);
    await mapController.init(latitude, longitude, autoHeightAdjustment);
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.syncMarkers`.
 */
async function syncMarkers(mapId: string, coords: Coord[], control: any): Promise<void> {
    await withMapController(mapId, async map => await map.syncMarkers(coords, control));
}

async function syncRoutes(mapId: string, routes: Route[]): Promise<void> {
    await withMapController(mapId, async map => await map.syncRoutes(routes));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.setMarker`.
 */
async function setMarker(mapId: string, cord: Coord, control: any): Promise<void> {
    await withMapController(mapId, async map => await map.setMarker(cord, control));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.addCustomControl`.
 */
async function addCustomControl(mapId: string, itemId: string, position: string): Promise<void> {
    await withMapController(mapId, async map => await map.addCustomControl(itemId, position));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.setBounds`.
 */
async function setBounds(mapId: string): Promise<void> {
    await withMapController(mapId, async map => await map?.setBounds());
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.setCenter`.
 */
async function setCenter(mapId: string, latitude: number, longitude: number): Promise<void> {
    await withMapController(mapId, async map => await map?.setCenter(latitude, longitude));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.setZoom`.
 */
async function setZoom(mapId: string, zoom: number): Promise<void> {
    await withMapController(mapId, async map => await map?.setZoom(zoom));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.deleteMarker`.
 */
async function deleteMarker(mapId: string, markerId: string): Promise<void> {
    await withMapController(mapId, async map => await map?.deleteMarker(markerId));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.clearMarkers`.
 */
async function clearMarkers(mapId: string): Promise<void> {
    await withMapController(mapId, async map => await map?.clearMarkers());
}

async function toggleMarkersDraggable(mapId: string, isDraggable: boolean, control: any): Promise<void> {
    await withMapController(mapId, async map => await map?.toggleMarkersDraggable(isDraggable, control));
}

async function resetMarkerCoords(mapId: string, lat: number, lng: number): Promise<void> {
    await withMapController(mapId, async map => await map?.resetMarkerCoords(lat, lng));
}

/**
 * Sets a marker on a specified map. Acts as a wrapper around `MapController.dispose`.
 */
async function dispose(mapId: string): Promise<void> {
    await withMapController(mapId, async map => {
        await map?.dispose();
        mapControllers.delete(mapId);
    });
}

/**
 * Executes a given asynchronous action on a `MapController` instance associated with a specific `mapId`.
 * This function checks if a `MapController` is registered for the provided `mapId` and, if so,
 * passes it to the provided action callback. If no corresponding `MapController` is found, it logs
 * a warning. This approach abstracts the error handling and lookup logic, simplifying operations on
 * `MapController` instances.
 * 
 * @param mapId The identifier of the map whose `MapController` is to be retrieved and used.
 * @param action An asynchronous callback function that performs operations on the retrieved `MapController`.
 * This function is expected to accept a `MapController` instance as its argument and return a Promise.
 * @returns A Promise that resolves once the action has been completed on the `MapController`, or nothing if
 * the `MapController` could not be found.
 */
async function withMapController(mapId: string, action: (controller: MapController) => Promise<void>) {
    const mapController = mapControllers.get(mapId);
    if (!mapController) {
        console.warn(`MapController for ${mapId} not found.`);
        return;
    }
    await action(mapController);
}

const mapsPublicFunctions = {
    controllers: mapControllers,
    initMap: initMap,
    setCenter: setCenter,
    setZoom: setZoom,
    setBounds: setBounds,
    setMarker: setMarker,
    syncMarkers: syncMarkers,
    syncRoutes: syncRoutes,
    addCustomControl: addCustomControl,
    deleteMarker: deleteMarker,
    clearMarkers: clearMarkers,
    toggleMarkersDraggable: toggleMarkersDraggable,
    resetMarkerCoords: resetMarkerCoords,
    dispose: dispose,
};

(window as any).maps = mapsPublicFunctions;